/**
 * SUPER_ADMIN Identification - Client-Safe Functions
 *
 * This module contains ONLY client-safe functions that can be used in Client Components.
 * These functions operate on session data (cached in JWT) and do NOT require server-only imports.
 *
 * **For server-side super admin validation**, use `lib/permissions/isSuperAdmin.ts` instead.
 *
 * @module lib/permissions/isSuperAdmin.client
 */

import type { Session } from 'next-auth';

/**
 * Synchronous SUPER_ADMIN check using cached session data
 *
 * **Use this in middleware and components** where session is available.
 * This avoids database queries by using the pre-computed permission flag
 * that was cached in the JWT token during login.
 *
 * **How it works:**
 * 1. During login: `canAccessAnyTenant = await userHasPermission(userId, 'system:tenant:accessAny')`
 * 2. Cached in JWT token for performance
 * 3. Available as `session.user.canAccessAnyTenant`
 * 4. This function reads from cache (O(1), no DB query)
 *
 * **Security:**
 * - ✅ Based on explicit role check (done during login)
 * - ✅ Cached for performance (no DB queries on every request)
 * - ✅ Safe default: Returns false if cache is missing
 * - ⚠️ Requires re-login after role changes (acceptable trade-off)
 *
 * @param session - NextAuth session object
 * @returns boolean - true if user has super_admin role (from JWT cache)
 *
 * @example
 * // In middleware
 * import { isSuperAdminSync } from '@/lib/permissions/isSuperAdmin.client';
 *
 * const session = await auth();
 * if (isSuperAdminSync(session)) {
 *   // Redirect to tenant selector
 *   return NextResponse.redirect('/select-tenant');
 * }
 *
 * @example
 * // In components (client-side)
 * const { data: session } = useSession();
 * if (session && isSuperAdminSync(session)) {
 *   // Show admin panel link
 * }
 */
export function isSuperAdminSync(session: Session | null): boolean {
  if (!session?.user) return false;

  // ============================================================================
  // CACHED PERMISSION CHECK (FROM JWT)
  // ============================================================================
  // The canAccessAnyTenant flag is computed during login using the async
  // isSuperAdmin() function (which queries UserRole table).
  //
  // This cached value provides O(1) performance while maintaining the same
  // explicit role-based security model.
  //
  // Safe default: If cache is missing/undefined → false (deny access)
  // ============================================================================

  return session.user.canAccessAnyTenant ?? false;
}

/**
 * Type guard to check if session user has super admin privileges
 *
 * Useful for TypeScript type narrowing in components and utilities.
 *
 * @param session - NextAuth session (can be null/undefined)
 * @returns Type predicate indicating if user is super admin
 *
 * @example
 * if (hasSuperAdminPrivileges(session)) {
 *   // TypeScript knows session is not null and user has privileges
 *   console.log(session.user.email);
 * }
 */
export function hasSuperAdminPrivileges(
  session: Session | null | undefined
): session is Session & { user: { canAccessAnyTenant: true } } {
  return session?.user?.canAccessAnyTenant === true;
}

/**
 * Legacy compatibility helper (DEPRECATED)
 *
 * Some existing code may use tenantId === null checks.
 * This helper provides backward compatibility during migration.
 *
 * @deprecated Use isSuperAdmin() or isSuperAdminSync() instead
 *
 * @param session - NextAuth session
 * @returns boolean - true if user might be super admin (tenantId is null)
 *
 * @example
 * // ❌ OLD CODE (Don't use in new code!)
 * if (session.user.tenantId === null) { ... }
 *
 * // ⚠️ TEMPORARY (During migration)
 * if (isLegacySuperAdmin(session)) { ... }
 *
 * // ✅ NEW CODE (Use this!)
 * if (isSuperAdminSync(session)) { ... }
 */
export function isLegacySuperAdmin(session: Session | null): boolean {
  return session?.user?.tenantId === null;
}
